// The accordion row of one aroma family: its colour rail, its drawing, its name, how many of its
// words are chosen and, closed, a summary of them. The colour is only ever a second signal: the
// drawing and the name carry the meaning, and the accessible themes draw the rail as a wider
// bordered swatch so it stays visible without relying on a tint.
import "./config";
import { View } from "tamagui";
import { AromaIcon, type AromaIconId } from "./AromaIcon";
import { Disclosure } from "./Disclosure";
import { Column, Fill, Row, VisuallyHidden } from "./layout";
import type { PaletteKey } from "./palette";
import { Badge } from "./surfaces";
import { Body, Caption } from "./text";

export interface AromaFamilyProps {
  name: string;
  icon: AromaIconId;
  /** The family's colour token (lib/aromaStyle.ts). */
  colour: PaletteKey;
  /** How many of the family's words are chosen. */
  count: number;
  /** Read out for the count ("2 chosen"); the badge itself is a figure only. */
  countLabel: string;
  /** The chosen words, shown under the name while the row is closed. */
  summary?: string | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  testId?: string | undefined;
  toggleTestId?: string | undefined;
  /** The family's words, shown while open. */
  children: React.ReactNode;
}

export function AromaFamily({
  name,
  icon,
  colour,
  count,
  countLabel,
  summary,
  open,
  onOpenChange,
  testId,
  toggleTestId,
  children,
}: AromaFamilyProps): React.JSX.Element {
  return (
    <Column
      borderRadius="3"
      borderWidth="var(--borderW)"
      borderColor={count > 0 ? "accentLine" : "line"}
      backgroundColor="surface"
      overflow="hidden"
      data-testid={testId}
    >
      <Row alignItems="stretch">
        <View
          aria-hidden
          alignSelf="stretch"
          flexShrink={0}
          width={{ default: 6, a11yLight: 16, a11yDark: 16 }}
          borderWidth={{ default: 0, a11yLight: 2, a11yDark: 2 }}
          borderColor="text"
          backgroundColor={colour}
        />
        <Fill paddingHorizontal="3" paddingVertical="1">
          <Disclosure
            open={open}
            onOpenChange={onOpenChange}
            toggleTestId={toggleTestId}
            header={
              <Row gap="3">
                <AromaIcon id={icon} size={32} />
                <Fill>
                  <Body weight="strong" textAlign="left">
                    {name}
                  </Body>
                  {summary === undefined || summary === "" || open ? null : (
                    <Caption truncate textAlign="left">
                      {summary}
                    </Caption>
                  )}
                </Fill>
                {count > 0 ? (
                  <>
                    <Badge aria-hidden>{count}</Badge>
                    <VisuallyHidden>{countLabel}</VisuallyHidden>
                  </>
                ) : null}
              </Row>
            }
          >
            <Column paddingBottom="2">{children}</Column>
          </Disclosure>
        </Fill>
      </Row>
    </Column>
  );
}
